import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { EnvelopeSimple, Sparkle, PaperPlaneTilt } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function DigestPreview() {
  const [preview, setPreview] = useState(null);
  const [status, setStatus] = useState({ email_enabled: false });
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get("/digest/preview").then((r) => setPreview(r.data)),
      api.get("/notify/status").then((r) => setStatus(r.data)),
    ]).finally(() => setLoading(false));
  }, []);

  const sendNow = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/digest/send");
      if (data.sent) toast.success("Digest sent to your inbox");
      else toast.error(data.note || "Not sent");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed");
    } finally { setSending(false); }
  };

  return (
    <div className="bg-white border border-[#EBE8E0] rounded-2xl p-6" data-testid="digest-section">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-2">
          <Sparkle size={22} weight="duotone" className="text-[#2C5545]" />
          <h2 className="font-heading text-xl font-bold">Your weekly digest</h2>
        </div>
        <button
          onClick={sendNow}
          disabled={sending}
          data-testid="digest-send-now"
          className="rounded-full bg-[#2C5545] hover:bg-[#224235] disabled:opacity-50 text-white font-semibold px-4 py-2 text-sm inline-flex items-center gap-2"
        >
          <PaperPlaneTilt size={16} weight="duotone" />
          {sending ? "Sending…" : "Send to my inbox"}
        </button>
      </div>
      <p className="text-sm text-[#576A62] mt-2">
        Delivered every Monday at 8:00 UTC to caregivers with email notifications enabled.
        Preview below shows everything from the last 7 days.
      </p>
      {!status.email_enabled && (
        <div className="mt-3 rounded-xl bg-[#D49A4C]/12 border border-[#D49A4C]/40 text-[#B37927] text-sm px-4 py-3 flex items-start gap-2">
          <EnvelopeSimple size={18} weight="duotone" className="shrink-0 mt-0.5" />
          <div>Email delivery is currently disabled. Add a <code className="font-mono text-xs">RESEND_API_KEY</code> to <code className="font-mono text-xs">backend/.env</code> to send digests.</div>
        </div>
      )}

      {loading ? (
        <div className="mt-6 h-64 rounded-xl bg-[#EBE8E0]/40 animate-pulse" />
      ) : (
        <>
          <div className="mt-6 flex items-center gap-3 text-sm">
            <span className="rounded-full bg-[#2C5545]/10 text-[#2C5545] font-semibold px-3 py-1" data-testid="digest-event-count">
              {preview?.data?.total_events || 0} events
            </span>
            <span className="rounded-full bg-[#EBE8E0] text-[#576A62] px-3 py-1" data-testid="digest-section-count">
              {preview?.data?.patient_sections?.length || 0} patient section(s)
            </span>
          </div>
          {preview?.data?.patient_sections?.length ? (
            <div
              className="mt-4 rounded-xl border border-[#EBE8E0] overflow-hidden bg-[#FDFBF7]"
              data-testid="digest-preview-html"
              // rendered from our own server; safe
              dangerouslySetInnerHTML={{ __html: preview.html }}
            />
          ) : (
            <div className="mt-4 rounded-xl border border-dashed border-[#EBE8E0] p-8 text-center text-[#576A62]">
              No activity in the last 7 days. Take a well-earned breath.
            </div>
          )}
        </>
      )}
    </div>
  );
}
